import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { HierarchyNode } from '../../api/analytics';
import { fmt, fmtCHF } from '../../utils/format';
import { getCantonName } from '../../utils/cantonData';
import styles from './OrgStatusCard.module.css';

interface Props {
  node: HierarchyNode;
  onClick?: (node: HierarchyNode) => void;
}

function getLevelLabel(level: string): string {
  if (level === 'federal') return 'Federal';
  if (level === 'canton') return 'Canton';
  if (level === 'commune') return 'Commune';
  return level;
}

function getLevelClass(level: string): string {
  if (level === 'federal') return styles.levelFederal ?? '';
  if (level === 'canton') return styles.levelCanton ?? '';
  if (level === 'commune') return styles.levelCommune ?? '';
  return '';
}

export function OrgStatusCard({ node, onClick }: Props) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick(node);
    } else {
      navigate(`/app/analytics/${node.id}`);
    }
  };

  const hasActivity = node.project_count > 0;

  return (
    <div
      className={styles.card}
      onClick={handleClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
      role="button"
      tabIndex={0}
    >
      <div className={styles.cardHeader}>
        <span className={`${styles.statusDot} ${hasActivity ? styles.statusActive : styles.statusEmpty}`} />
        <span className={styles.orgName}>
          {node.level === 'canton' && node.code ? getCantonName(node.code, i18n.language) : node.name}
        </span>
        <span className={`${styles.levelBadge} ${getLevelClass(node.level)}`}>
          {getLevelLabel(node.level)}
        </span>
      </div>

      <div className={styles.metricsRow}>
        <div className={styles.metric}>
          <span className={styles.metricValue}>{fmt(node.total_logements)}</span>
          <span className={styles.metricLabel}>Logements</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricValue}>{fmt(node.total_emplois)}</span>
          <span className={styles.metricLabel}>Emplois</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricValue}>{node.project_count}</span>
          <span className={styles.metricLabel}>Projects</span>
        </div>
      </div>

      <div className={styles.costRow}>
        <span className={styles.costValue}>
          {node.total_cost > 0 ? fmtCHF(node.total_cost) : '\u2014'}
        </span>
        <span className={styles.densityValue}>
          {node.avg_density > 0 ? `${node.avg_density} log/ha` : ''}
        </span>
      </div>

      {node.children.length > 0 && (
        <span className={styles.childCount}>
          {node.children.length} {node.level === 'federal' ? 'cantons' : 'communes'}
          <span className={styles.chevron}>&rsaquo;</span>
        </span>
      )}
    </div>
  );
}
